(() => {
  'use strict';

  const normalizeType = value => {
    const type = String(value || '').trim().toLowerCase();
    if (['dining chair', 'dining chairs', 'accent chair', 'accent chairs'].includes(type)) return 'dining-chairs';
    if (['bar stool', 'bar stools', 'counter stool', 'counter stools'].includes(type)) return 'counter-stools';
    if (['office chair', 'office chairs', 'home office chair', 'home office chairs'].includes(type)) return 'office-chairs';
    if (['ottoman', 'ottomans'].includes(type)) return 'ottomans';
    return type.replace(/\s+/g, '-');
  };
  const normalizeColour = value => String(value || '').trim().toLowerCase();
  const moneyToCents = value => Math.round(Number(value) * 100);
  const matchesVariant = (variant, filters) => (!filters.colours.size || filters.colours.has(normalizeColour(variant.colour))) && (filters.minPrice === null || variant.price >= filters.minPrice) && (filters.maxPrice === null || variant.price <= filters.maxPrice);
  const qualifyingVariants = (product, filters) => product.variants.filter(variant => matchesVariant(variant, filters));
  const productMatches = (product, filters) => (!filters.types.size || filters.types.has(normalizeType(product.type))) && qualifyingVariants(product, filters).length > 0;
  const selectedVariant = (product, filters) => {
    const qualifying = qualifyingVariants(product, filters);
    return qualifying.find(variant => String(variant.id) === String(product.selectedVariantId)) || qualifying[0] || null;
  };
  const sortProducts = (products, sortBy, filters) => [...products].sort((left, right) => {
    const leftPrice = selectedVariant(left, filters)?.price || 0;
    const rightPrice = selectedVariant(right, filters)?.price || 0;
    if (sortBy === 'price-ascending') return leftPrice - rightPrice || left.index - right.index;
    if (sortBy === 'price-descending') return rightPrice - leftPrice || left.index - right.index;
    if (sortBy === 'title-ascending') return left.title.localeCompare(right.title) || left.index - right.index;
    if (sortBy === 'title-descending') return right.title.localeCompare(left.title) || left.index - right.index;
    return left.index - right.index;
  });
  const clientSorts = new Set(['price-ascending', 'price-descending', 'title-ascending', 'title-descending']);
  const api = { normalizeType, normalizeColour, moneyToCents, matchesVariant, qualifyingVariants, productMatches, selectedVariant, sortProducts };
  if (typeof module !== 'undefined') module.exports = api;
  if (typeof document === 'undefined') return;

  const parseVariant = button => ({ id: button.dataset.variantId, colour: button.dataset.variantColour, price: Number(button.dataset.variantPrice), priceLabel: button.dataset.variantPriceLabel, available: button.dataset.variantAvailable === 'true', url: button.dataset.variantUrl, image: button.dataset.variantImage || '', imageAlt: button.dataset.variantImageAlt || '' });
  const cardProduct = card => {
    if (card._archiologyProduct) return card._archiologyProduct;
    card._archiologyProduct = { card, index: Number(card.dataset.productIndex), title: card.querySelector('h2').textContent.trim(), type: card.dataset.productType, selectedVariantId: card.dataset.defaultVariantId, variants: [...card.querySelectorAll('[data-variant-id]')].map(parseVariant) };
    return card._archiologyProduct;
  };
  const readFilters = form => {
    const values = selector => [...form.querySelectorAll(selector)].filter(input => input.checked).map(input => input.dataset.clientValue);
    const number = selector => { const value = form.querySelector(selector)?.value.trim(); return value === '' || Number.isNaN(Number(value)) ? null : moneyToCents(value); };
    return { types: new Set(values('[data-client-filter="type"]').map(normalizeType)), colours: new Set(values('[data-client-filter="colour"]').map(normalizeColour)), minPrice: number('[data-client-filter="price-min"]'), maxPrice: number('[data-client-filter="price-max"]') };
  };
  const setVariant = (product, variant) => {
    if (!variant) return;
    product.selectedVariantId = variant.id;
    const card = product.card;
    card.querySelectorAll('[data-variant-id]').forEach(button => button.setAttribute('aria-pressed', String(button.dataset.variantId === String(variant.id))));
    card.querySelector('[data-product-price]').textContent = variant.priceLabel;
    card.querySelector('[data-selected-variant-label]').textContent = variant.colour;
    card.querySelectorAll('[data-product-link]').forEach(link => { link.href = variant.url; });
    const image = card.querySelector('[data-product-image], .archiology-furniture__product-image img');
    if (image && variant.image) {
      image.removeAttribute('srcset');
      image.removeAttribute('sizes');
      image.src = variant.image;
      image.alt = variant.imageAlt || product.title;
    }
    card.querySelector('[data-product-sold-out]').hidden = variant.available;
  };
  const syncFromUrl = form => {
    const params = new URLSearchParams(window.location.search);
    const types = new Set(params.getAll('arch_type').map(normalizeType));
    const colours = new Set(params.getAll('arch_colour').map(normalizeColour));
    form.querySelectorAll('[data-client-filter="type"]').forEach(input => { input.checked = types.has(normalizeType(input.dataset.clientValue)); });
    form.querySelectorAll('[data-client-filter="colour"]').forEach(input => { input.checked = colours.has(normalizeColour(input.dataset.clientValue)); });
    const min = form.querySelector('[data-client-filter="price-min"]');
    const max = form.querySelector('[data-client-filter="price-max"]');
    const sort = form.querySelector('[data-sort-select]');
    if (min) min.value = params.get('arch_min_price') || '';
    if (max) max.value = params.get('arch_max_price') || '';
    if (sort && params.get('sort_by')) sort.value = params.get('sort_by');
  };
  const updateUrl = form => {
    const url = new URL(window.location.href);
    const filters = readFilters(form);
    ['arch_type', 'arch_colour', 'arch_min_price', 'arch_max_price'].forEach(name => url.searchParams.delete(name));
    filters.types.forEach(type => url.searchParams.append('arch_type', type));
    filters.colours.forEach(colour => url.searchParams.append('arch_colour', colour));
    if (filters.minPrice !== null) url.searchParams.set('arch_min_price', String(filters.minPrice / 100));
    if (filters.maxPrice !== null) url.searchParams.set('arch_max_price', String(filters.maxPrice / 100));
    const sort = form.querySelector('[data-sort-select]').value;
    if (sort) url.searchParams.set('sort_by', sort);
    history.replaceState({}, '', url);
  };
  const init = section => {
    if (section.dataset.ready || section.dataset.clientCatalogComplete !== 'true') return;
    section.dataset.ready = 'true';
    const form = section.querySelector('[data-collection-form]');
    const grid = section.querySelector('[data-product-grid]');
    const cards = [...grid.querySelectorAll('[data-product-card]')].map(cardProduct);
    const count = section.querySelector('[data-result-count]');
    const empty = section.querySelector('[data-empty-message]');
    const addGeneratedFilter = (kind, label, values) => {
      const filter = form.querySelector(`[data-generated-filter="${kind}"]`);
      if (!filter || form.querySelector(`[data-filter-menu="${kind}"]:not([data-generated-filter])`)) return;
      const list = filter.querySelector('.archiology-furniture__filter-values');
      [...values].sort((left, right) => left.localeCompare(right)).forEach(value => {
        const row = document.createElement('label');
        const input = document.createElement('input');
        input.type = 'checkbox';
        input.dataset.clientFilter = kind;
        input.dataset.clientValue = value;
        const text = document.createElement('span');
        text.textContent = value;
        row.append(input, text);
        list.append(row);
      });
      filter.querySelector('summary').textContent = label;
      filter.dataset.filterMenu = kind;
      filter.hidden = false;
    };
    addGeneratedFilter('type', 'Type', new Set(cards.map(product => product.type)));
    addGeneratedFilter('colour', 'Colour', new Set(cards.flatMap(product => product.variants.map(variant => variant.colour))));
    const apply = ({ writeUrl = true } = {}) => {
      const filters = readFilters(form);
      const matches = cards.filter(product => productMatches(product, filters));
      const sorted = sortProducts(matches, form.querySelector('[data-sort-select]').value, filters);
      cards.forEach(product => { product.card.hidden = !matches.includes(product); });
      sorted.forEach(product => { setVariant(product, selectedVariant(product, filters)); grid.append(product.card); });
      cards.forEach(product => product.card.querySelectorAll('[data-variant-id]').forEach(element => {
        const variant = product.variants.find(candidate => String(candidate.id) === element.dataset.variantId);
        element.setAttribute('aria-disabled', String(!matchesVariant(variant, filters)));
      }));
      count.textContent = `${matches.length} ${matches.length === 1 ? 'piece' : 'pieces'}`;
      empty.hidden = matches.length !== 0;
      section.querySelector('[data-clear-filters]').hidden = !filters.types.size && !filters.colours.size && filters.minPrice === null && filters.maxPrice === null;
      if (writeUrl) updateUrl(form);
    };
    form.querySelector('[data-apply-filters]').hidden = true;
    form.addEventListener('submit', event => { event.preventDefault(); apply(); });
    form.querySelectorAll('[data-client-filter="type"], [data-client-filter="colour"]').forEach(input => input.addEventListener('change', apply));
    form.querySelector('[data-sort-select]').addEventListener('change', () => {
      const sort = form.querySelector('[data-sort-select]').value;
      if (!clientSorts.has(sort)) {
        updateUrl(form);
        window.location.assign(window.location.href);
        return;
      }
      apply();
    });
    let priceTimer;
    form.querySelectorAll('[data-client-filter="price-min"], [data-client-filter="price-max"]').forEach(input => { input.addEventListener('input', () => { clearTimeout(priceTimer); priceTimer = setTimeout(apply, 250); }); input.addEventListener('change', apply); });
    form.querySelector('[data-clear-filters]').addEventListener('click', event => { event.preventDefault(); form.querySelectorAll('input[type="checkbox"]').forEach(input => { input.checked = false; }); form.querySelectorAll('[data-client-filter="price-min"], [data-client-filter="price-max"]').forEach(input => { input.value = ''; }); apply(); });
    section.querySelectorAll('[data-variant-id]').forEach(button => {
      button.setAttribute('role', 'button');
      button.title = button.getAttribute('aria-label');
      button.addEventListener('keydown', event => { if (event.key === ' ') { event.preventDefault(); button.click(); } });
      button.addEventListener('click', event => {
      event.preventDefault();
      if (button.getAttribute('aria-disabled') === 'true') return;
      const product = cardProduct(button.closest('[data-product-card]'));
      const variant = product.variants.find(candidate => String(candidate.id) === button.dataset.variantId);
      if (variant) setVariant(product, variant);
      });
    });
    const menus = section.querySelectorAll('[data-filter-menu]');
    menus.forEach(menu => menu.addEventListener('toggle', () => { if (menu.open) menus.forEach(other => { if (other !== menu) other.open = false; }); }));
    syncFromUrl(form);
    apply({ writeUrl: false });
    window.addEventListener('popstate', () => {
      syncFromUrl(form);
      if (!clientSorts.has(form.querySelector('[data-sort-select]').value)) {
        window.location.assign(window.location.href);
        return;
      }
      apply({ writeUrl: false });
    });
  };
  document.querySelectorAll('.archiology-furniture').forEach(init);
  document.addEventListener('shopify:section:load', event => event.target.querySelectorAll('.archiology-furniture').forEach(init));
})();
